import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Layers, Package, Eye, MessageCircle, TrendingUp, AlertTriangle, PlusCircle, CalendarClock, Wallet } from "lucide-react";
import api from "@/lib/api";
import { rupee, daysLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useAddProduct } from "@/lib/useAddProduct";

function Stat({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <div className="bg-card border rounded-xl p-4">
      <Icon className={`h-5 w-5 ${color}`} />
      <div className="mt-2 font-heading text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

export default function SellerDashboard() {
  const { sub, access } = useOutletContext();
  const nav = useNavigate();
  const addProduct = useAddProduct(access, sub?.slots);
  const [stats, setStats] = useState({ views: 0, clicks: 0, products: [] });
  const [lastVisit, setLastVisit] = useState(null);
  const [sold, setSold] = useState({ sold_value: 0, sold_qty: 0 });

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setStats({
        products: data,
        views: data.reduce((a, p) => a + (p.views || 0), 0),
        clicks: data.reduce((a, p) => a + (p.whatsapp_clicks || 0), 0),
      });
    });
    api.get("/seller/last-visit").then(({ data }) => setLastVisit(data)).catch(() => {});
    api.get("/seller/sold-summary").then(({ data }) => setSold(data)).catch(() => {});
  }, []);

  if (!sub) return <div className="text-muted-foreground">Loading…</div>;
  const s = sub.slots;
  const activeCount = stats.products.filter((p) => p.status === "active").length;

  const noSub = access === "restricted";
  const pct = s.total_slots ? Math.round((s.used_slots / s.total_slots) * 100) : 0;
  const nearlyFull = !noSub && s.total_slots > 0 && pct >= 80;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Dashboard</h1>
          <p className="text-muted-foreground text-sm font-medium">Overview of your Cleartile stock and subscription.</p>
        </div>
        {lastVisit && (
          <div className="bg-card border rounded-xl px-4 py-2.5 flex items-center gap-2.5" data-testid="last-visit-card">
            <CalendarClock className="h-5 w-5 text-primary" />
            {lastVisit.last_visit ? (
              <div className="text-sm">
                <div className="font-bold" data-testid="last-visit-days">
                  {lastVisit.days_ago === 0 ? "Last visit: earlier today" : `${lastVisit.days_ago} day${lastVisit.days_ago === 1 ? "" : "s"} since your last visit`}
                </div>
                <div className="text-xs text-muted-foreground font-medium">on {new Date(lastVisit.last_visit).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
              </div>
            ) : (
              <div className="text-sm font-bold">Welcome — this is your first visit 🎉</div>
            )}
          </div>
        )}
      </div>

      {noSub && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-900">No active subscription</p>
            <p className="text-sm text-amber-800">Purchase a yearly plan to unlock product slots and start listing your stock.</p>
          </div>
          <Button data-testid="dash-subscribe-btn" onClick={() => nav("/seller/subscription")} className="font-bold">Subscribe Now</Button>
        </div>
      )}


      {sub.subscription && (
        <div className="bg-slate-900 text-white rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/50 font-bold">Current Plan</div>
            <div className="font-heading text-xl font-extrabold">{sub.subscription.name || sub.slots.package_label}</div>
            {sub.subscription.features && <div className="text-xs text-primary font-semibold">{sub.subscription.features}</div>}
            <div className="text-sm text-white/70 font-medium">{daysLabel(sub.days_remaining)} · valid 1 year</div>
          </div>
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 font-bold capitalize">{access.replace("_", " ")}</Badge>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Package} label="Active Products" value={activeCount} color="text-emerald-600" />
        <Stat icon={Eye} label="Total Views" value={stats.views} color="text-blue-600" />
        <Stat icon={MessageCircle} label="WhatsApp Clicks" value={stats.clicks} color="text-[#25D366]" />
        <Stat icon={Wallet} label="Stock Sold Value" value={rupee(sold.sold_value)} color="text-primary" />
      </div>

      <div className="bg-card border rounded-2xl p-6" data-testid="slot-status-indicator">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Product Slots</h2>
          <Button variant="outline" onClick={() => nav("/seller/subscription")} className="font-semibold">Manage Slots</Button>
        </div>
        <div className="mb-2 font-heading font-extrabold" data-testid="slot-usage-text">Used {s.used_slots} of {s.total_slots} slots</div>
        <Progress value={pct} className={`h-3 ${nearlyFull ? "[&>div]:bg-amber-500" : ""}`} data-testid="slot-usage-bar" />
        <div className="mt-2 text-sm font-semibold flex justify-between">
          <span>{pct}% used</span><span className="text-muted-foreground">{s.available_slots} available</span>
        </div>
        {nearlyFull && (
          <div className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="slot-nearly-full-nudge">
            <p className="text-sm font-semibold text-amber-900">{s.available_slots <= 0 ? "All your slots are used." : `Only ${s.available_slots} slot${s.available_slots === 1 ? "" : "s"} left.`} Buy an add-on package to keep listing.</p>
            <Button size="sm" onClick={() => nav("/seller/subscription")} data-testid="slot-nudge-addon-btn" className="font-bold">Get Add-on</Button>
          </div>
        )}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><div className="text-muted-foreground text-xs font-semibold">Included</div><div className="font-bold">{s.included_slots}</div></div>
          <div><div className="text-muted-foreground text-xs font-semibold">Additional</div><div className="font-bold">{s.additional_slots}</div></div>
          <div><div className="text-muted-foreground text-xs font-semibold">Base Subscription</div><div className="font-bold">{rupee(s.base_subscription)}</div></div>
          <div><div className="text-muted-foreground text-xs font-semibold">Yearly Billing</div><div className="font-bold text-primary">{rupee(s.total_yearly_billing)}</div></div>
        </div>
      </div>

      <Button data-testid="dash-add-product" onClick={addProduct} className="font-bold gap-2" disabled={access === "view_only"}>
        <PlusCircle className="h-4 w-4" /> Add New Product
      </Button>
    </div>
  );
}
